import { NavLink, Outlet, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { supabase, q } from '../lib/supabase';
import { useAuth } from '../lib/AuthContext';
import { useAsync } from '../lib/useAsync';
import { ErrorBanner, Spinner } from '../components/Shared';
import Icon from '../components/Icon';

const TABS = [
  { to: 'squad', label: 'Squad', icon: 'people' },
  { to: 'balance', label: 'Balance', icon: 'shuffle' },
  { to: 'log', label: 'Log', icon: 'clipboard' },
  { to: 'history', label: 'History', icon: 'clock' },
];

/** Everything below the layout reads the group and the viewer's role from here. */
export function useGroup() {
  return useOutletContext();
}

export default function GroupLayout() {
  const { groupId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data, loading, error, reload } = useAsync(async () => {
    const membership = await q(
      supabase
        .from('group_members')
        .select('id, role, groups(id, name, join_code)')
        .eq('group_id', groupId)
        .eq('profile_id', user.id)
        .maybeSingle(),
      'Loading this group'
    );
    if (!membership || !membership.groups) {
      throw new Error("This group doesn't exist, or you're not a member of it.");
    }
    return {
      group: membership.groups,
      role: membership.role,
      myMemberId: membership.id,
      isAdmin: membership.role === 'admin',
    };
  }, [groupId, user.id]);

  if (loading) return <Spinner label="Opening group…" />;

  if (error) {
    return (
      <div className="page page--narrow">
        <ErrorBanner error={error} onRetry={reload} />
        <button type="button" className="btn btn--outline" onClick={() => navigate('/groups')}>
          Back to your groups
        </button>
      </div>
    );
  }

  return (
    <div className="shell">
      <header className="shell__top">
        <button type="button" className="shell__back" onClick={() => navigate('/groups')}>
          <Icon name="chevronLeft" size={18} />
          <span className="shell__groupname">{data.group.name}</span>
        </button>
        <span className="shell__code" title="Invite code">
          {data.group.join_code}
        </span>
      </header>

      <nav className="shell__nav" aria-label="Sections">
        {TABS.map((t) => (
          <NavLink key={t.to} to={t.to} className={({ isActive }) => `navtab ${isActive ? 'navtab--on' : ''}`}>
            <Icon name={t.icon} size={19} />
            <span>{t.label}</span>
          </NavLink>
        ))}
      </nav>

      <main className="shell__main">
        <Outlet context={{ ...data, reloadGroup: reload }} />
      </main>
    </div>
  );
}
